import React from 'react';
import { X, FileText, User, Home, Calendar, Euro, Ruler, TrendingUp, Bell, CheckCircle, AlertTriangle, XCircle, Clock } from 'lucide-react';

/**
 * PaymentTrafficModal – Zahlungsampel-Maske pro Mieter.
 * Zeigt Stammdaten, 12-Monats-Zahlungsverlauf (Soll/Ist + NK-Kurve),
 * Verspätungstage und die Ampel-Klassifikation (grün/gelb/rot).
 * Rein zur Anzeige/Entscheidungshilfe: keine automatische Mahnung,
 * keine Verrechnung, keine Buchung.
 */
export default function PaymentTrafficModal({ unit, months = [], ampel, credits = [], year, loading, error, onClose, onOpenPdf, onDun }) {
  const fmt = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
  const fmtDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split('-');
    return d ? `${d}.${m}.${y}` : iso;
  };
  const leaseText = () => {
    const s = unit?.lease_start ? fmtDate(unit.lease_start) : null;
    const e = unit?.lease_end ? fmtDate(unit.lease_end) : null;
    if (s && e) return `${s} – ${e}`;
    if (s) return `${s} – offen`;
    return 'nicht hinterlegt';
  };
  const level = ampel?.level || 'green';
  const levelMeta = {
    green: { label: 'Zuverlässiger Zahler', color: '#16a34a', bg: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200', Icon: CheckCircle },
    yellow: { label: 'Gelegentlich auffällig', color: '#d97706', bg: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200', Icon: AlertTriangle },
    red: { label: 'Zahlungsproblem', color: '#dc2626', bg: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200', Icon: XCircle },
  }[level];
  const LevelIcon = levelMeta.Icon;

  // ── Graph: Soll/Ist-Balken + NK-Kurve (SVG) ──
  const maxRent = Math.max(unit?.monthly_rent || 0, ...months.map((m) => Math.max(Number(m.expected_rent || 0), Number(m.rent_paid || 0))), 1);
  const maxNk = Math.max(...months.map((m) => Number(m.nk_paid || 0)), 1);
  const W = 560, H = 150, PAD_L = 8, PAD_B = 18;
  const barW = Math.max(8, (W - PAD_L * 2) / Math.max(months.length, 1) - 6);
  const step = (W - PAD_L * 2) / Math.max(months.length, 1);
  const yScale = (v, max) => H - PAD_B - (Number(v || 0) / max) * (H - PAD_B - 8);
  const nkPoints = months.map((m, i) => `${PAD_L + i * step + barW / 2},${yScale(m.nk_paid, maxNk) * 0.55 + 8}`).join(' ');

  return (
    <div className="fixed inset-0 z-[998] flex items-start justify-center bg-black/30 p-4 pt-10">
      <div className="max-h-[88vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        {/* Kopf */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2.5">
            <Home className="mt-0.5 h-5 w-5 text-violet-600" />
            <div>
              <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                {unit?.unit_label || 'Einheit'}
              </h2>
              <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                <User className="h-3.5 w-3.5" />
                {unit?.tenant_name || 'Mieter unbekannt'}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="mt-4 text-sm text-slate-500">Lade Zahlungsverlauf…</div>}
        {error && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/20 dark:text-amber-200">
            Daten derzeit nicht verfügbar.
          </div>
        )}

        {!loading && !error && unit && (
          <>
            {/* Ampel-Zeile */}
            <div className={`mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg px-3 py-2 ${levelMeta.bg}`}>
              <div className="flex items-center gap-2">
                <LevelIcon className="h-5 w-5" />
                <div>
                  <div className="text-sm font-semibold">{levelMeta.label}</div>
                  <div className="text-xs opacity-80">
                    {ampel ? `${ampel.on_time_months}/${ampel.months_observed} Monate pünktlich · Ø max. ${ampel.max_days_late} Tage Verzug · ${ampel.dunning_count} Mahnung(en)` : ''}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-xs opacity-80"><TrendingUp className="h-3.5 w-3.5" />Score</div>
                <div className="text-lg font-bold leading-tight">{ampel ? ampel.score : '–'}/100</div>
              </div>
            </div>

            {/* Graph */}
            <div className="mt-3 rounded-lg border border-gray-100 p-3 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Zahlungsverlauf {year || new Date().getFullYear()}</div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-slate-300 opacity-70" />Soll</span><span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-emerald-500" />pünktlich</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-amber-500" />verspätet</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-red-400" />offen</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-3 bg-sky-500" />NK</span>
                </div>
              </div>
              <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" aria-label="Zahlungsverlauf">
                {/* Soll-Linie */}
                <line x1={PAD_L} x2={W - PAD_L} y1={yScale(unit.monthly_rent, maxRent)} y2={yScale(unit.monthly_rent, maxRent)} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 3" />
                {/* Balken */}
                {months.map((m, i) => {
                  const x = PAD_L + i * step + 3;
                  const hSoll = (Number(m.expected_rent || 0) / maxRent) * (H - PAD_B - 8);
                  const hPaid = (Number(m.rent_paid || 0) / maxRent) * (H - PAD_B - 8);
                  const yPaid = H - PAD_B - hPaid;
                  const barColor = m.future ? '#cbd5e1' : (m.rent_count === 0 ? '#f87171' : (m.on_time === false ? '#f59e0b' : '#22c55e'));
                  const ySoll = H - PAD_B - hSoll;
                  const diff = Number(m.rent_paid || 0) - Number(m.expected_rent || 0);
                  return (
                    <g key={m.month}>
                      {!m.future && Number(m.expected_rent || 0) > 0 && (
                        <rect x={x - 1} y={ySoll} width={barW + 2} height={hSoll} rx="2" fill="#cbd5e1" opacity="0.35">
                          <title>{`${m.month}: Soll ${fmt(m.expected_rent)}`}</title>
                        </rect>
                      )}
                      <rect x={x} y={yPaid} width={barW} height={Math.max(hPaid, m.rent_count > 0 ? 2 : 3)} rx="2" fill={barColor} opacity="0.9">
                        <title>{`${m.month}: Soll ${fmt(m.expected_rent)} · Ist ${fmt(m.rent_paid)}${m.days_late != null && m.days_late > 0 ? ` · +${m.days_late} Tage` : ''}${m.rent_count === 0 ? ' · keine Zahlung' : ''}`}</title>
                      </rect>
                      {!m.future && Math.abs(diff) >= 0.005 && (
                        <text x={x + barW / 2} y={Math.max(yPaid, ySoll) - 3} textAnchor="middle" fontSize="7" fontWeight="600" fill={diff < 0 ? '#dc2626' : '#ffffff'}>
                          {diff > 0 ? '+' : ''}{Math.round(diff)} €
                        </text>
                      )}
                      {(i % 2 === 0) && (
                        <text x={x + barW / 2} y={H - 5} textAnchor="middle" fontSize="8" fill="#94a3b8">{m.month.slice(5)}</text>
                      )}
                    </g>
                  );
                })}
                {/* NK-Kurve */}
                {months.some((m) => m.nk_paid > 0) && (
                  <polyline points={nkPoints} fill="none" stroke="#0ea5e9" strokeWidth="1.5" opacity="0.85" />
                )}
              </svg>
            </div>

            {/* Verlaufstabelle (kompakt) */}
            <div className="mt-3 rounded-lg border border-gray-100 p-3 dark:border-slate-700">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monatsübersicht</div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th className="py-1 pr-2 font-medium">Monat</th>
                      <th className="py-1 pr-2 font-medium">Soll</th>
                      <th className="py-1 pr-2 font-medium">Ist</th>
                      <th className="py-1 pr-2 font-medium">Differenz</th>
                      <th className="py-1 pr-2 font-medium">Verzug</th>
                      <th className="py-1 pr-2 font-medium">NK</th>
                      <th className="py-1 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                    {months.slice().reverse().map((m) => (
                      <tr key={m.month}>
                        <td className="py-1 pr-2 font-medium text-slate-700 dark:text-slate-300">{m.month}</td>
                        <td className="py-1 pr-2 text-slate-600 dark:text-slate-400">{fmt(m.expected_rent)}</td>
                        <td className="py-1 pr-2 text-slate-600 dark:text-slate-400">{m.rent_count > 0 ? fmt(m.rent_paid) : '–'}</td>
                        <td className={`py-1 pr-2 font-medium ${m.future ? 'text-slate-400' : (Number(m.rent_paid||0) >= Number(m.expected_rent||0) ? 'text-white' : 'text-red-500 dark:text-red-400')}`}>{m.future ? '–' : fmt(Number(m.rent_paid||0) - Number(m.expected_rent||0))}</td>
                        <td className="py-1 pr-2 text-slate-600 dark:text-slate-400">{m.days_late > 0 ? `+${m.days_late} T` : (m.rent_count > 0 ? '0 T' : '–')}</td>
                        <td className="py-1 pr-2 text-sky-700 dark:text-sky-300">{m.nk_paid > 0 ? fmt(m.nk_paid) : '–'}</td>
                        <td className="py-1">
                          {m.future ? (
                            <span className="inline-flex items-center gap-1 text-slate-400"><Clock className="h-3 w-3" />noch offen</span>
                          ) : m.rent_count === 0 ? (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"><Clock className="h-3 w-3" />offen</span>
                          ) : m.on_time === false ? (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertTriangle className="h-3 w-3" />verspätet</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle className="h-3 w-3" />pünktlich</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Stammdaten */}
            <div className="mt-3 rounded-lg border border-gray-100 p-3 dark:border-slate-700">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mieter & Vertrag</div>
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-slate-500"><Calendar className="h-3.5 w-3.5" />Mietlaufzeit</span><span className="font-medium text-slate-900 dark:text-white">{leaseText()}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-slate-500"><Euro className="h-3.5 w-3.5" />Kaltmiete</span><span className="font-medium text-slate-900 dark:text-white">{fmt(unit.cold_rent)}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-slate-500"><Euro className="h-3.5 w-3.5" />Nebenkosten</span><span className="font-medium text-slate-900 dark:text-white">{fmt(unit.utilities_cost)}</span></div>
                <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-slate-500"><Euro className="h-3.5 w-3.5" />Gesamtmiete</span><span className="font-semibold text-slate-900 dark:text-white">{fmt(unit.monthly_rent)}</span></div>
                {(unit.living_space_m2 > 0 || unit.rooms > 0) && (
                  <div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-slate-500"><Ruler className="h-3.5 w-3.5" />Wohnfläche</span><span className="font-medium text-slate-900 dark:text-white">{unit.living_space_m2 > 0 ? `${unit.living_space_m2.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} m²` : '–'}{unit.rooms > 0 ? ` · ${unit.rooms} Zimmer` : ''}</span></div>
                )}
              </div>
            </div>

            {/* NK-Guthaben */}
            {credits.length > 0 && (
              <div className="mt-3 rounded-lg border border-emerald-100 p-3 dark:border-emerald-900/40">
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">NK-Guthaben</div>
                <div className="mt-1.5 space-y-1 text-sm text-emerald-800 dark:text-emerald-300">
                  {credits.map((c, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span>NK {c.billing_year || '–'}{c.due_date ? ` · ${fmtDate(c.due_date)}` : ''}</span>
                      <span className="font-semibold">+{fmt(c.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3 text-[11px] text-slate-400">
              Anzeige & Entscheidungshilfe – es wird nichts automatisch gemahnt, verrechnet oder gebucht.
            </div>
          </>
        )}

        {/* Aktionen */}
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {level === 'red' && onDun && (
            <button
              onClick={onDun}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
            >
              <Bell className="h-4 w-4" /> Mahnung erstellen
            </button>
          )}
          {level === 'yellow' && onDun && (
            <button
              onClick={onDun}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-300"
            >
              <Bell className="h-4 w-4" /> Zahlungserinnerung / Mahnung
            </button>
          )}
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
